/**
 * StudySync Main App Component
 * Root component with routing, theming, and authentication
 */

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box } from "@mui/material";
import { Toaster } from "react-hot-toast";
import {
  SecureAuthProvider,
  useSecureAuth,
} from "./contexts/SecureAuthContext";
import { UserProvider, useUser } from "./contexts/UserContext";
import { theme } from "./theme";

// Import components (will be created next)
import LoadingSpinner from "./components/common/LoadingSpinner";
import AccountUnavailable from "./components/common/AccountUnavailable";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";

// Page components (will be created)
// LandingPage is the production landing (real RetroUI + Tailwind). It self-imports
// retroui.css + the self-hosted fonts, so it needs no theme wrapper.
import LandingPage from "./components/landing/LandingPage";
import ScheduleDemoPage from "./components/landing/ScheduleDemoPage";
import PeoplePage from "./components/landing/PeoplePage";
import ResearcherAuth from "./components/researcher/ResearcherAuth";
import ForgotPassword from "./components/researcher/ForgotPassword";
import ResearcherDashboard from "./components/researcher/ResearcherDashboard";
import StudyDashboard from "./components/researcher/StudyDashboard";
import AlertsTab from "./components/researcher/AlertsTab";
import ParticipantProfile from "./components/researcher/ParticipantProfile";
import ParticipantSuccess from "./components/participant/ParticipantSuccess";
import ReconnectApp from "./components/participant/ReconnectApp";
import FitbitCollectionInvitationStart from "./components/participant/FitbitCollectionInvitationStart";
import FitbitCollectionProfileCompletion from "./components/participant/FitbitCollectionProfileCompletion";
import { UltrahumanInvitationStart, UltrahumanProfileCompletion } from "./components/participant/UltrahumanEnrollment";
import AssessmentInvitationStart from "./components/participant/AssessmentInvitationStart";
import GoogleHealthReauthorization from "./components/google-health/GoogleHealthReauthorization";
import TemplateEditorPage from "./pages/TemplateEditorPage";
import { StudySectionRedirect } from "./components/researcher/study/StudySectionPanels";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AppEnrollmentRedirect from "./pages/AppEnrollmentRedirect";
import StudyMgmtDashboard from "./components/study-management/StudyMgmtDashboard";
import ConsentFormCreatePage from "./components/consent_management/components/researcher/ConsentFormCreatePage";
import ParticipantConsentPage from "./components/consent_management/components/participant/ParticipantConsentPage";

// Master Account and Registration components
import { MasterDashboard, AdherenceMonitor } from "./components/admin";
import ResearcherRegistration from "./components/researcher/ResearcherRegistration";
import ApplicationStatus from "./components/researcher/ApplicationStatus";
import SmartRedirect from "./components/common/SmartRedirect";
import SupportWidget from "./components/support/SupportWidget";
import SpotlightProvider from "./components/spotlight/SpotlightProvider";

// DEV ONLY: expose a helper to dump the Firebase ID token in console
import { getAuth, onAuthStateChanged } from "firebase/auth";

// LOCAL-ONLY presentation scene (3 watch renderings + massive labels), lazy-loaded so
// three.js/r3f stays out of the main bundle. Not linked from any app UI.
const PresentationWatches = React.lazy(
  () => import("./pages/PresentationWatches"),
);

if (process.env.NODE_ENV === "development") {
  (window as any).dumpIdToken = async () => {
    const auth = getAuth();
    const user =
      auth.currentUser ||
      (await new Promise<any>((res) => onAuthStateChanged(auth, res)));
    if (!user) throw new Error("Not signed in");
    const token = await user.getIdToken(true);
    return token;
  };
}

// Protected Route Components
interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Legacy protected route - kept for potential future use
// const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
//   const { currentUser, loading } = useAuth();
//   if (loading) return <LoadingSpinner />;
//   if (!currentUser) return <Navigate to="/researcher/auth" replace />;
//   return <>{children}</>;
// };

// Master Account Protected Route
const MasterRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user: currentUser, isLoading: authLoading } = useSecureAuth();
  const { loading: userLoading, isMasterAccount } = useUser();

  if (authLoading || userLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    // Store the current URL with query params for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem("redirectAfterLogin", currentPath);
    return <Navigate to="/researcher/auth" replace />;
  }

  if (!isMasterAccount) {
    return <Navigate to="/researcher/dashboard" replace />;
  }

  return <>{children}</>;
};

// Researcher Protected Route
const ResearcherRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user: currentUser, isLoading: authLoading } = useSecureAuth();
  const { loading: userLoading, isApprovedResearcher, userProfile, profileUnavailable } = useUser();
  const location = useLocation();

  if (authLoading || userLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    // Store the current URL with query params for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem("redirectAfterLogin", currentPath);
    return <Navigate to="/researcher/auth" replace />;
  }

  // An account that could not be loaded is not an account without a profile.
  if (profileUnavailable) {
    return <AccountUnavailable />;
  }

  // CRITICAL: Only approved researchers can access researcher routes
  if (!isApprovedResearcher) {
    // If user has noprofile status, redirect to registration
    if (userProfile?.status === "noprofile" || !userProfile) {
      // Allow them to stay on registration page, but redirect from everywhere else
      if (location.pathname !== "/researcher/register") {
        return <Navigate to="/researcher/register" replace />;
      }
      return <>{children}</>;
    }

    // If user has pending/rejected status, redirect to application-status
    if (
      userProfile &&
      (userProfile.status === "pending" || userProfile.status === "rejected")
    ) {
      // Allow them to stay on application-status page, but redirect from everywhere else
      if (location.pathname !== "/researcher/application-status") {
        return <Navigate to="/researcher/application-status" replace />;
      }
      return <>{children}</>;
    }

    // Default: redirect to registration if status is unknown
    return <Navigate to="/researcher/register" replace />;
  }

  // Approved researcher - allow access to all researcher routes
  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isLoading: authLoading } = useSecureAuth();
  const { loading: userLoading } = useUser();
  const location = useLocation();
  // Landing routes render full-bleed without the authenticated-app chrome
  // (header/footer) — the landing page brings its own navigation.
  // Local presentation scenes render full-bleed without the header and footer.
  const isPresentationRoute = location.pathname.startsWith("/present/");
  // Normalize a trailing slash so e.g. "/people/" is still recognized as a landing
  // route — otherwise the authenticated-app <Header/> renders ON TOP of the landing's
  // own nav (a duplicate nav bar) for any landing URL reached with a trailing slash.
  const landingPath = location.pathname.replace(/\/+$/, "") || "/";
  const isLandingRoute =
    isPresentationRoute ||
    landingPath === "/" ||
    landingPath === "/schedule-demo" ||
    landingPath === "/people" ||
    landingPath === "/terms-of-service" ||
    landingPath === "/privacy-policy";
  const isPublicEnrollmentRoute = [
    "/participant/enroll", "/fitbit/enroll", "/fitbit/enroll/unavailable", "/fitbit/enroll/profile",
    "/ultrahuman/enroll", "/ultrahuman/enroll/unavailable", "/ultrahuman/enroll/profile",
    "/assessment/start",
  ].includes(landingPath);

  // Public landing routes bring their own nav and don't depend on auth/user state,
  // so they must render immediately — never block them on the auth spinner, or the
  // marketing page flashes "Loading..." until Firebase resolves (and hangs there if
  // auth can't initialize). Authenticated app routes still gate on loading below.
  // Participant invitations and their cookie-bound profile forms do not use a
  // researcher session. Auth refreshes must not unmount them after the opaque
  // invitation has been removed from the URL.
  if ((authLoading || userLoading) && !isLandingRoute && !isPublicEnrollmentRoute) {
    return <LoadingSpinner />;
  }

  return (
    <SpotlightProvider>
      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        {!isLandingRoute && <Header />}

        <Box component="main" sx={{ flexGrow: 1, py: isLandingRoute ? 0 : 3 }}>
          <Routes>
            {/* Landing Page — the real RetroUI landing (self-imports retroui.css + fonts) */}
            <Route path="/" element={<LandingPage />} />

            {/* Schedule a Demo — standalone landing-styled page; its own custom URL */}
            <Route path="/schedule-demo" element={<ScheduleDemoPage />} />

            {/* People — standalone landing-styled team page; reached from nav/footer */}
            <Route path="/people" element={<PeoplePage />} />

            {/* Local DEV-ONLY presentation scene — 3 watch renderings + massive labels
                (~2:1). Guarded on NODE_ENV so it is DEAD-CODE-ELIMINATED from production
                builds: `process.env.NODE_ENV` is the string "production" there, so this
                becomes `{false && …}`, the <Route> is never registered, the lazy
                PresentationWatches chunk is never referenced, and /present/watches simply
                falls through to the catch-all redirect to "/". */}
            {process.env.NODE_ENV === "development" && (
              <Route
                path="/present/watches"
                element={
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <PresentationWatches />
                  </React.Suspense>
                }
              />
            )}

            {/* Terms of Service */}
            <Route path="/terms-of-service" element={<TermsOfService />} />

            {/* Privacy Policy */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* Authentication */}
            <Route path="/researcher/auth" element={<ResearcherAuth />} />
            <Route
              path="/researcher/forgot-password"
              element={<ForgotPassword />}
            />

            {/* Smart Dashboard Redirect */}
            <Route path="/dashboard" element={<SmartRedirect />} />

            {/* Alerts Tab */}
            <Route
              path="/dashboard/alerts"
              element={
                <ResearcherRoute>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "100vh",
                    }}
                  >
                    <AlertsTab />
                  </Box>
                </ResearcherRoute>
              }
            />

            {/* Registration - Protected route for authenticated users only */}
            <Route
              path="/researcher/register"
              element={
                <ResearcherRoute>
                  <ResearcherRegistration />
                </ResearcherRoute>
              }
            />

            {/* Application Status - Protected route for authenticated users */}
            <Route
              path="/researcher/application-status"
              element={
                <ResearcherRoute>
                  <ApplicationStatus />
                </ResearcherRoute>
              }
            />

            {/* Master Account Routes */}
            <Route
              path="/master/dashboard"
              element={
                <MasterRoute>
                  <MasterDashboard />
                </MasterRoute>
              }
            />

            {/* Researcher Routes - Protected by approval status */}
            <Route
              path="/researcher/dashboard"
              element={
                <ResearcherRoute>
                  <ResearcherDashboard />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId"
              element={
                <ResearcherRoute>
                  <StudyDashboard />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/template-editor"
              element={
                <ResearcherRoute>
                  <TemplateEditorPage />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/preferences"
              element={
                <ResearcherRoute>
                  <StudySectionRedirect section="preferences" />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study-adherence-monitor"
              element={
                <ResearcherRoute>
                  <StudySectionRedirect section="adherence" />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/visualizations"
              element={
                <ResearcherRoute>
                  <StudySectionRedirect section="visualization" />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/data-coverage"
              element={
                <ResearcherRoute>
                  <StudySectionRedirect section="coverage" />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/consent-management"
              element={
                <ResearcherRoute>
                  <StudySectionRedirect section="consent" />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/study/:studyId/consent-management/create"
              element={
                <ResearcherRoute>
                  <ConsentFormCreatePage />
                </ResearcherRoute>
              }
            />
            <Route
              path="/researcher/participant-profile/:participantId/:studyId"
              element={
                <ResearcherRoute>
                  <ParticipantProfile />
                </ResearcherRoute>
              }
            />

            {/* Portfolio Overview - Master Only */}
            <Route
              path="/study-management/analytics"
              element={
                <MasterRoute>
                  <StudyMgmtDashboard />
                </MasterRoute>
              }
            />
            {/* Adherence Monitor - Admin Dashboard */}
            <Route
              path="/admin-adherence-monitor"
              element={
                <MasterRoute>
                  <AdherenceMonitor />
                </MasterRoute>
              }
            />

            {/* Participant Routes */}
            <Route path="/app/enroll" element={<AppEnrollmentRedirect />} />
            <Route
              path="/participant/enroll"
              element={<FitbitCollectionInvitationStart />}
            />
            {/* Fresh Fitbit collection routes are intentionally isolated from
                the legacy participant and Google Health enrollment flow. */}
            <Route
              path="/fitbit/enroll"
              element={<FitbitCollectionInvitationStart />}
            />
            <Route
              path="/fitbit/enroll/unavailable"
              element={<FitbitCollectionInvitationStart />}
            />
            <Route
              path="/fitbit/enroll/profile"
              element={<FitbitCollectionProfileCompletion />}
            />
            <Route path="/participant/consent/:token" element={<ParticipantConsentPage />} />
            <Route path="/ultrahuman/enroll" element={<UltrahumanInvitationStart />} />
            <Route path="/ultrahuman/enroll/unavailable" element={<UltrahumanInvitationStart />} />
            <Route path="/ultrahuman/enroll/profile" element={<UltrahumanProfileCompletion />} />
            <Route path="/assessment/start" element={<AssessmentInvitationStart />} />
            {/* Legacy Fitbit flow routes no longer execute their historical
                components. Do not forward their query parameters because old
                callbacks can contain provider credentials. */}
            <Route
              path="/participant/flow"
              element={<Navigate to="/participant/enroll" replace />}
            />
            <Route
              path="/participant/verify"
              element={<Navigate to="/participant/enroll" replace />}
            />
            <Route
              path="/participant/verify-email"
              element={<Navigate to="/participant/enroll" replace />}
            />
            <Route
              path="/participant/info"
              element={<Navigate to="/participant/enroll" replace />}
            />
            <Route
              path="/participant/success"
              element={<ParticipantSuccess />}
            />
            <Route
              path="/participant/reconnect-app"
              element={<ReconnectApp />}
            />

            <Route
              path="/google-health/reauthorize"
              element={<GoogleHealthReauthorization />}
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>

        {!isLandingRoute && <Footer />}
      </Box>
    </SpotlightProvider>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster position="top-right" toastOptions={{ duration: 5000 }} />
      <SecureAuthProvider>
        <UserProvider>
          <Router>
            <AppRoutes />
            <SupportWidget />
          </Router>
        </UserProvider>
      </SecureAuthProvider>
    </ThemeProvider>
  );
};

export default App;
